const halfDivisor = 2;

class Util {
    /**
     * Determine if a string is null, undefined, empty string, or a string containing only whitespace
     */
    public isEmptyUnsetOrWhitespace(subject: any) {
        return subject === undefined || subject === null || subject.toString().trim().length === 0;
    }

    /**
     * Get a promise that resolves after the specified milliseconds
     */
    public sleep(milliseconds: number) {
        return new Promise<void>(r => setTimeout(r, milliseconds));
    }

    /**
     * Runs setTimeout, and returns a disconnect function for easy cancelability.
     */
    public timeout<T extends Function>(callback: T, milliseconds: number) {
        const callbackHandle = setTimeout(
            () => {
                callback();
            },
            milliseconds
        );
        return () => {
            clearTimeout(callbackHandle);
        };
    }

    /**
     * Given a string with snake-case-text in it, return camelCaseText.
     */
    public snakeToCamelCase(snakeCaseText: string) {
        const parts = snakeCaseText.split('-');
        let result = parts[0];
        for (let i = 1; i < parts.length; i++) {
            const part = parts[i];
            result += part[0].toUpperCase() + part.substring(1);
        }
        return result;
    }

    /**
     * Run a callback every `frequencyMilliseconds` until `totalMilliseconds` have passed
     * until any value other than undefined is returned from callback
     * @param callback - function to call. Can return a promise, which will be waited on
     */
    public async retryUntilValue<T extends Function>(callback: T, totalMilliseconds: number, frequencyMilliseconds = 15) {
        const end = Date.now() + totalMilliseconds;
        while (Date.now() < end) {
            const result = await Promise.resolve(callback());
            if (result !== undefined) {
                return result;
            }
            await this.sleep(frequencyMilliseconds);
        }
    }

    /**
     * Run a task, and if it takes longer than `totalMilliseconds`, reject the promise.
     * If successful, return the returnvalue from the callback
     */
    public runOrTimeout<T>(callback: () => T | Promise<T>, totalMilliseconds: number): Promise<T> {
        return new Promise(
            (resolve, reject) => {
                let isTimedOut = false;
                // set a timeout. if it expires before the return has happened, then fail
                const unsetTimeout = this.timeout(
                    () => {
                        isTimedOut = true;
                        reject(new Error(`Task took longer than the max ${totalMilliseconds}ms allowed`));
                    },
                    totalMilliseconds
                );

                // wait for the results
                Promise.resolve(callback()).then(
                    result => {
                        // do nothing, the timeout already took care of failing
                        if (isTimedOut) {
                            return;
                        } else {
                            resolve(result);
                        }
                    },
                    e => {
                        reject(e);
                        unsetTimeout();
                    }
                );
            }
        );
    }

    /**
     * Looks for the closet ancestor, taking in to account shadow roots.
     */
    public getClosest(el: HTMLElement, tagName: string) {
        let currentEl;
        while (!currentEl || currentEl.nodeName !== tagName.toUpperCase()) {
            currentEl = el.closest(tagName);
            if (!currentEl) {
                currentEl = (<any>el.getRootNode()).host as HTMLElement;
                if (currentEl) {
                    el = currentEl;
                } else {
                    break;
                }
            }
        }
        return currentEl;
    }

    /**
     * Load a script into the document head if it isn't loaded yet
     * @param scriptUrl - url of the script
     * @param scriptId - a unique id for the script
     * @param loadedCheck - function to call to check if the script is loaded yet. Can be a promise.
     * @param loadWaitMilliseconds - how long to wait for the script to load. Default: 15 seconds.
     */
    public async loadScript<T extends Function>(scriptUrl: string, scriptId: string, loadedCheck: T, loadWaitMilliseconds: number = 15000) {
        const initiallyLoaded = await loadedCheck();
        if (!document.getElementById(scriptId) && !initiallyLoaded) {
            const script = document.createElement('script');
            script.setAttribute('src', scriptUrl);
            script.setAttribute('type', 'text/javascript');
            script.setAttribute('id', scriptId);
            document.head.appendChild(script);
        }
        await this.retryUntilValue(loadedCheck, loadWaitMilliseconds);
    }

    /**
     * Returns a function that, as long as it continues to be invoked, will not
     * be triggered. The function will be called after it stops being called for
     * N milliseconds. If `immediate` is passed, trigger the function on the
     * leading edge, instead of the trailing.
     * @param immediate - Execute call immediately
     */
    public debounce<T = any[], R = void>(func: (args?: T) => R, wait: number, immediate?: boolean) {
        let timeout;
        return function() {
            const later = function() {
                timeout = null;
                if (!immediate) { func.call(this, ...arguments); }
            };
            const callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later.bind(this, ...arguments), wait);
            if (callNow) {
                func.call(this, ...arguments);
            }
        };
    }

    /**
     * Returns a function that, when invoked, will only be triggered at most once
     * during a given window of time. Normally, the throttled function will run as
     * much as it can, without ever going more than once per `wait` duration.
     */
    public throttle<T = any[], R = void>(func: (args?: T) => R, wait: number) {
        let context, args, result;
        let timeout = null;
        let previous = 0;
        const later = function() {
            previous = Date.now();
            timeout = null;
            result = func.apply(context, args);
            if (!timeout) { context = args = null; }
        };
        return function() {
            const now = Date.now();
            if (!previous) { previous = now; }
            const remaining = wait - (now - previous);
            context = this;
            args = arguments;
            if (remaining <= 0 || remaining > wait) {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }
                previous = now;
                result = func.apply(context, args);
                if (!timeout) { context = args = null; }
            } else if (!timeout) {
                timeout = setTimeout(later, remaining);
            }
            return result;
        };
    }

    /**
     * Return the middle most item of an array
     */
    public getMiddleItem<T = any>(arr: T[]): T {
        let retVal: T = null;
        if (arr && arr.length) {
            retVal = arr[arr.length > 1 ? Math.floor(arr.length / halfDivisor) : 0];
        }
        return retVal;
    }

    /**
     * Get an item from an array, wrapping back to the beginning if the index is greater than the array length
     */
    public getItemWithOverflow<T = any>(arr: T[], index: number) {
        while (index >= arr.length) {
            index = index - arr.length;
        }
        return arr[index];
    }

    /**
     * Remove items in one array from another
     * @param array Array to remove items from
     * @param remove Array of items to be removed
     */
    public removeFromArray(array: any[], remove: any[]) {
        return array.filter(val => !remove.includes(val));
    }

    /**
     * Returns the given string capitalized
     * @param text String to be capitalized
     */
    public capitalize(text: string): string {
        return text[0].toUpperCase() + text.slice(1);
    }

    /**
     * Returns the given source as a new context
     * @param source The object to copy
     */
    public deepCopy<T>(source: T): T {
        return Array.isArray(source)
            ? source.map(item => this.deepCopy(item))
            : source instanceof Date
                ? new Date(source.getTime())
                : source && typeof source === 'object'
                    ? Object.getOwnPropertyNames(source).reduce((o, prop) => {
                        Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop));
                        o[prop] = this.deepCopy(source[prop]);
                        return o;
                    }, Object.create(Object.getPrototypeOf(source)))
                    : source as T;
    }

    /**
     * Deep clone an object. Exception is thrown if any part of the input value is not serializable.
     * Some ESRI objects have a clone() method. If present, that function is used to perform the copy.
     * @param object The object to deep clone/copy
     */
    public deepClone<T>(object: T): T {
        if (typeof (object as any).clone === 'function') {
            return (object as any).clone() as T;
        } else {
            return (window as any).structuredClone(object) as T;
        }
    }

    /**
     * Determine if a value is an even or odd number
     * Does not catch if a string isn't a number, just returns false
     */
    public isEven(value: string | number) {
        let toCheck: number;
        if (typeof value === 'string') {
            toCheck = parseFloat(value);
            if (isNaN(toCheck)) {
                return false;
            }
        } else {
            toCheck = value;
        }
        return toCheck % 2 === 0;
    }

    /**
     * Compares if one string is included in another regardless
     * of capitalization and surrounding whitespace
     */
    public LooseIncludes(source: string, search: string) {
        return source.toLowerCase().trim().includes(search.toLowerCase().trim());
    }
}

export let util = new Util();
